import { useCallback, useEffect, useState } from 'react';
import { generatePath, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '@store';
import {
  ajvResolver,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
} from '@ui/src';
import { DictionaryItem, DictionaryItemFormValues } from '../../types';
import {
  useCreateDictionaryItemMutation,
  useUpdateDictionaryItemMutation,
  useDeleteDictionaryItemMutation,
  useLazyGetDictionaryItemsQuery,
  useLazyGetDictionaryItemQuery,
} from '../../store/service';
import { clearDictionaryItemPanelState } from '../../store/slice';

import ROUTES from '../../../../routes';

const schema = {
  type: 'object',
  properties: {
    value: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Value is required',
    },
    riskScore: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Risk Score is required',
    },
  },
  required: ['value', 'riskScore'],
};

const useDictionaryItemForm = ({
  dictionaryId,
  itemId,
  dictionaryItem,
  takeCount,
  activePermissions,
}: {
  dictionaryId?: string;
  itemId?: string;
  dictionaryItem?: DictionaryItem;
  takeCount?: number;
  activePermissions?: PermissionEnum[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [dirtyPopuplIsOpen, setDirtyPopupIsOpen] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [createAnother, setCreateAnother] = useState(false);

  const {
    control,
    formState: { errors, isDirty },
    setValue,
    handleSubmit,
    reset,
    setError,
  } = useForm<DictionaryItemFormValues>({
    resolver: ajvResolver(schema),
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { search, state } = useLocation();

  const [getDictionaryItem, { isLoading, isFetching }] =
    useLazyGetDictionaryItemQuery();
  const [
    createDictionaryItem,
    { isLoading: createInProgress, isSuccess: createIsSuccess },
  ] = useCreateDictionaryItemMutation();
  const [updateDictionaryItem, { isLoading: updateInProgress }] =
    useUpdateDictionaryItemMutation();
  const [
    deleteDictionaryItem,
    { isLoading: deleteInProgress, isSuccess: deleteIsSuccess },
  ] = useDeleteDictionaryItemMutation();
  const [getDictionaryItems] = useLazyGetDictionaryItemsQuery();

  const onSubmit = handleSubmit(async (values) => {
    setCreateAnother(false);
    setGlobalError('');

    const body: DictionaryItemFormValues = {
      value: values.value,
      riskScore: Number(values.riskScore),
    };

    if (values.code) {
      body.code = values.code;
    } else if (!values.code && dictionaryItem?.code) {
      body.code = null;
    }

    if (values.description) {
      body.description = values.description;
    } else if (!values.description && dictionaryItem?.description) {
      body.description = null;
    }

    let response;

    if (itemId) {
      response = (await updateDictionaryItem({
        id: dictionaryId as string,
        itemId,
        body,
      })) as { error?: ResponseError };
    } else {
      response = (await createDictionaryItem({
        id: dictionaryId as string,
        body,
      })) as { error?: ResponseError };
    }

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError,
      });
    } else if (!response?.error && itemId) {
      reset({
        value: values?.value || '',
        code: values?.code || '',
        description: values?.description || '',
        riskScore: values?.riskScore ? values?.riskScore?.toString() : '',
      });
    }
  });

  const onCreate = handleSubmit(async (values) => {
    setCreateAnother(true);
    setGlobalError('');

    const body: DictionaryItemFormValues = {
      value: values.value,
      riskScore: Number(values.riskScore),
    };

    if (values.code) {
      body.code = values.code;
    }

    if (values.description) {
      body.description = values.description;
    }

    const response = (await createDictionaryItem({
      id: dictionaryId as string,
      body,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError,
        setGlobalError,
      });
    } else {
      reset({
        value: '',
        code: '',
        description: '',
        riskScore: '',
      });
    }
  });

  const onDelete = async () => {
    setDeleteError('');

    const response = (await deleteDictionaryItem({
      id: dictionaryId as string,
      itemId: itemId as string,
    })) as { error?: ResponseError };

    if (response?.error) {
      responseErrorsResolver({
        errors: response?.error,
        setError: () => {},
        setGlobalError: setDeleteError,
      });
    }
  };

  const onDuplicateClick = () => {
    setGlobalError('');
    dispatch(clearDictionaryItemPanelState());
    reset({
      value: '',
      code: '',
      description: '',
      riskScore: '',
    });

    setTimeout(() => {
      navigate(
        `${generatePath(ROUTES.DICTIONARIES_LIST_ITEMS, { dictionaryId: dictionaryId as string })}${search || ''}`,
      );
    }, 100);

    setTimeout(() => {
      navigate(
        `${generatePath(ROUTES.DICTIONARIES_LIST_ITEMS_CREATE, { dictionaryId: dictionaryId as string })}${search || ''}`,
        {
          state: {
            ...dictionaryItem,
            duplicate: true,
          },
        },
      );
    }, 200);
  };

  const handleClose = () => {
    setGlobalError('');
    setDeleteError('');
    setDirtyPopupIsOpen(false);
    setPanelIsOpen(false);

    dispatch(clearDictionaryItemPanelState());

    setTimeout(() => {
      navigate(
        `${generatePath(ROUTES.DICTIONARIES_LIST_ITEMS, { dictionaryId: dictionaryId as string })}${search || ''}`,
      );
    }, 100);
  };

  const getDictionaryItemsAction = useCallback(async () => {
    await getDictionaryItems({
      id: dictionaryId as string,
      query: `?take=${takeCount}${search ? search.replace('?', '&') : ''}`,
    });
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);
  }, []);

  useEffect(() => {
    if (
      panelIsOpen &&
      itemId &&
      activePermissions?.includes(PermissionEnum.DICTIONARIES_ITEMS_LIST)
    ) {
      getDictionaryItem({
        id: dictionaryId as string,
        itemId,
      });
    }
  }, [panelIsOpen, itemId, activePermissions]);

  useEffect(() => {
    if (deleteIsSuccess || createIsSuccess) {
      if (deleteIsSuccess) {
        setDeletePopupVisible(false);
      }

      if (deleteIsSuccess || (createIsSuccess && !createAnother)) {
        handleClose();
      }

      getDictionaryItemsAction();
    }
  }, [deleteIsSuccess, createIsSuccess, createAnother]);

  useEffect(() => {
    if (panelIsOpen && state && state.duplicate) {
      reset({
        value: state?.value || '',
        code: state?.code || '',
        description: state?.description || '',
        riskScore: state?.riskScore ? state?.riskScore?.toString() : '',
      });
    }
  }, [panelIsOpen, state]);

  useEffect(() => {
    if (dictionaryItem) {
      reset({
        value: dictionaryItem?.value || '',
        code: dictionaryItem?.code || '',
        description: dictionaryItem?.description || '',
        riskScore: dictionaryItem?.riskScore
          ? dictionaryItem?.riskScore?.toString()
          : '',
      });
    }
  }, [dictionaryItem]);

  return {
    panelIsOpen,
    setDirtyPopupIsOpen,
    dirtyPopuplIsOpen,
    deletePopupVisible,
    setDeletePopupVisible,
    globalError,
    deleteError,
    control,
    errors,
    isDirty,
    setValue,
    createInProgress,
    updateInProgress,
    deleteInProgress,
    onSubmit,
    onDelete,
    handleClose,
    isLoading,
    isFetching,
    onCreate,
    onDuplicateClick,
    duplicate: state?.duplicate,
  };
};

export default useDictionaryItemForm;
